// Shared, flat-shaded rock geometries for the coast (one geometry per silhouette, instanced): a tall spire, a
// flat-topped mesa, a twin-peaked stack and a low boulder cluster. Unit footprint (radius 0.5) standing on y = 0;
// instances scale them. Vertices are jittered deterministically so no two silhouettes in a kind look machined,
// and every face carries a baked tone (bakeShade) so unlit rocks still read as solid.

import * as THREE from 'three';
import { bakeShade, merge } from '../TerrainScatterMaterials.js';

const hash = (n: number) => {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
};

/** Jitter every vertex of an indexed-or-not geometry by up to (jx, jy, jz), keyed by position so shared corners stay shared. */
function jitter(g: THREE.BufferGeometry, jx: number, jy: number, jz: number, seed: number) {
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i);
    const y = p.getY(i);
    const z = p.getZ(i);
    const k = seed + Math.round(x * 97) * 3.1 + Math.round(y * 89) * 5.3 + Math.round(z * 83) * 7.7;
    // Keep the base ring planted and the very top point where it is.
    const live = y > 0.02 ? 1 : 0.15;
    p.setXYZ(
      i,
      x + (hash(k) - 0.5) * jx * live,
      y + (hash(k + 1) - 0.5) * jy * live,
      z + (hash(k + 2) - 0.5) * jz * live,
    );
  }
  p.needsUpdate = true;
  return g;
}

function spire(seed: number, tilt = 0): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.1, 0.5, 1, 7, 4, false).translate(0, 0.5, 0);
  jitter(g, 0.16, 0.1, 0.16, seed);
  // A slight lean makes the stack read as eroded, not turned on a lathe.
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) p.setX(i, p.getX(i) + tilt * p.getY(i));
  return bakeShade(g, { bottom: 0.5, top: 1.05, jitter: 0.2, seed: seed + 3 });
}

function mesa(seed: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(0.4, 0.5, 1, 9, 3, false).translate(0, 0.5, 0);
  jitter(g, 0.14, 0.08, 0.14, seed);
  return bakeShade(g, { bottom: 0.52, top: 1.08, jitter: 0.18, seed: seed + 5 });
}

function boulder(seed: number): THREE.BufferGeometry {
  const parts = [
    [0, 0, 0, 0.5],
    [0.32, 0, 0.12, 0.34],
    [-0.22, 0, -0.26, 0.3],
  ].map(([x, , z, r], i) => {
    const g = new THREE.DodecahedronGeometry(r!, 0).scale(1, 0.8, 1).translate(x!, r! * 0.55, z!);
    return bakeShade(jitter(g, 0.1, 0.08, 0.1, seed + i * 11), {
      bottom: 0.55,
      top: 1.05,
      jitter: 0.22,
      seed: seed + i * 13,
    });
  });
  return merge(parts);
}

/** One geometry per rock kind. Built lazily, once. */
let cache: Record<'spire' | 'mesa' | 'twin' | 'boulder', THREE.BufferGeometry> | null = null;
export function rockGeometries() {
  if (cache) return cache;
  // Twin: a tall spire and a shorter, thinner one leaning the other way (both already baked + non-indexed).
  const tall = spire(101, 0.05);
  const short = spire(131, -0.08).scale(0.62, 0.7, 0.62).translate(0.34, 0, 0.1);
  cache = {
    spire: spire(53, 0.04),
    mesa: mesa(71),
    twin: merge([tall, short]),
    boulder: boulder(211),
  };
  return cache;
}

/**
 * Flat foam ring (annulus in the XZ plane, unit outer radius ~1.0): attribute `aRad` runs 0 at the inner edge to
 * 1 at the outer one so the shader can fade it. Scaled per rock to ~1.6x its footprint.
 */
export function foamRingGeometry(segments = 28): THREE.BufferGeometry {
  const inner = 0.62;
  const pos: number[] = [];
  const rad: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const c = Math.cos(a);
    const s = Math.sin(a);
    pos.push(c * inner, 0, s * inner, c * 1.18, 0, s * 1.18);
    rad.push(0, 1);
    if (i < segments) {
      const k = i * 2;
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aRad', new THREE.Float32BufferAttribute(rad, 1));
  g.setIndex(idx);
  return g;
}
